import NotificationBar from 'components/notifications/NotificationBar';
import FloatingTooltip from 'components/watchlist/FloatingTooltip';
import WatchlistTracking from 'components/watchlist/WatchlistTracking';
import HttpService from 'services/HttpService';
import bfCachePersists from 'helpers/bfCachePersists';
import UrlHelper from 'helpers/UrlHelper';
import { getLocale } from 'services/LocalizationService.js';

const CONTENT_SHOW = 'show';
const CONTENT_MOVIE = 'movie';

export default class WatchlistCta {
    constructor() {
        this.contentType = null;
        this.contentItem = null;
        this.contentId = null;
        this.watchlistData = null;
        this._ = null;
        this._cta = null;
        this._tooltipTime = 4500;
        this._tooltipTimeout = null;
        this._http = new HttpService();
        this._notificationBar = null;
        this._floatingTooltip = null;
        this._tracking = null;
        this._apiErrorMsg = getLocale('sorry_we_cant_add_or_remove_items_right_now');
        this._apiErrorTrackingMessage = 'API Error';
        this._limitErrorTrackingMessage = 'Limit Error';
        this._isSafari = this.isSafari();
        this._referrerUrl = document.referrer;
        this._actionTrigger = false;
        this._aaLink = $('#hero-content .cta-box a.button[aa-link]').attr('aa-link') !== undefined ? $('#hero-content .cta-box a.button[aa-link]').attr('aa-link') :  $('#watchlistCta').attr('aa-link');

        // Avoids cached page when going back/forth in browser
        //
        bfCachePersists(() => {
            if (this._isSafari) window.location.reload();
            this.handleWatchlistCtaState();
        });

        // Adds event listener for popstate (back button)
        window.onpopstate = function () {
            window.location.href = document.referrer;
        };

        // Initializes the component when DOM is loaded
        if (document.readyState === 'complete') {
            // Inits tracking
            this.init();
        } else {
            document.addEventListener('DOMContentLoaded', () => this.init());
        }
    }

    init() {
        const self = this;

        // Inits tracking
        this.initTracking();

        // Validate that there's watchlist component element
        if (this.getComponentElement() === null) return false;

        self._ = this.getComponentElement();
        self._cta = self._.querySelector('#watchlistCta');
        self.contentType = CBS.Registry.pageType === 'SHOWHOME' ? CONTENT_SHOW : CONTENT_MOVIE;
        self.contentItem = self.contentType === CONTENT_SHOW ? CBS.Registry.Show : null;
        self.watchlistData = CBS.Registry.Watchlist;
        self.contentId =
            self.contentType === CONTENT_SHOW ? self.contentItem.id : self.watchlistData.content_id;

        this._notificationBar = new NotificationBar();
        this._floatingTooltip = new FloatingTooltip(this.contentType === CONTENT_MOVIE);

        let cta = this._cta;
        cta.parentElement.addEventListener('click', (e) => {
            e.preventDefault();
            self.ctaClickHandler();
        });
        cta.parentElement.addEventListener('mouseenter', () => {
            self.showTooltip();
            setTimeout(() => cta.classList.add('hovered'), 80);
        });
        cta.parentElement.addEventListener('mouseleave', () => cta.classList.remove('hovered'));
        cta.parentElement.addEventListener('blur', () => console.log('blur event'));

        // Checks if item is already on watchlist
        this.handleWatchlistCtaState();

        // Adding "initialized" class
        cta.parentElement.classList.add('initialized');
        // Adding class to identify content type page
        cta.parentElement.classList.add(
            self.contentType === CONTENT_SHOW ? 'in_show_page' : 'in_movie_page'
        );
        // Adding "content id" and "content type"
        cta.setAttribute('data-content_type', self.contentType);
        cta.setAttribute('data-content_id', self.contentId);
    }

    getComponentElement() {
        return document.querySelector('div.watchlist_wrapper');
    }

    handleWatchlistCtaState() {
        let cta = this._cta;
        this.isOnWatchlist()
            .then((res) => {
                if (res.success) {
                    let watchlistId = res.data.watchListId;
                    cta.setAttribute('data-watchlist_id', watchlistId);
                    setTimeout(() => cta.classList.add('in_watchlist'), 50);
                } else {
                    if (cta.classList.contains('in_watchlist'))
                        cta.classList.remove('in_watchlist');
                }
            })
            .catch((err) => {
                console.log(`Watchlist CTA - ${err.message}`);
            });
    }

    isOnWatchlist() {
        const xhrUrl = `${CBS.Registry.region.prefix}/user-profile/xhr/watchlist/${
            this.contentType
        }/${this.contentId}/?timestamp=${new Date().getTime()}`;
        return this._http.doGet(xhrUrl);
    }

    ctaClickHandler() {
        if (this._actionTrigger) return false;

        let cta = this._cta;
        this._actionTrigger = true;

        // adds to watchlist or removes from watchlist
        cta.classList.contains('in_watchlist') ? this.removeFromWatchlist() : this.addToWatchlist();
    }

    showTooltip() {
        this._cta.setAttribute(
            'title',
            this._cta.classList.contains('in_watchlist')
                ? getLocale('remove_from_watchlist')
                : getLocale('add_to_watchlist')
        );
    }

    showLimitReachedTooltip(message = '') {
        let cta = this._cta;

        if (this.isMobileLandscapeMax()) {
            this._floatingTooltip.slideIn(message);
        } else {
            let tooltip = cta.parentElement.querySelector('span.tooltip');
            tooltip.innerHTML = `<div>${message}</div>`;
            tooltip.classList.add('visible');
            this.createTooltipTimeout(tooltip);
        }

        // tracks limit reached API error
        if (this._tracking) {
            this._tracking.trackApiError({
                ctaText: this._limitErrorTrackingMessage,
                ...this.getTrackingParams()
            });
        }
    }

    showErrorMessage() {
        this._notificationBar.showMessage(this._apiErrorMsg, false);

        // tracks API error
        if (this._tracking) {
            this._tracking.trackApiError({
                ctaText: this._apiErrorTrackingMessage,
                ...this.getTrackingParams()
            });
        }
    }

    addToWatchlist() {
        const self = this;
        let cta = this._cta;

        // Adds to watchlist
        self.watchlistAddXhrCall()
            .then((res) => {
                if (res.success) {
                    cta.classList.remove('hovered');
                    cta.classList.add('in_watchlist');
                    // tracks add action
                    if (this._tracking) this._tracking.trackAddAction(this.getTrackingParams());
                    // Adds pushstate (history) on watchlist modification
                    if (this.isReferrerHome()) {
                        if (this._isSafari) {
                            window.history.pushState({}, '', document.referrer);
                            return true;
                        }
                        window.history.replaceState({}, document.title, window.location.href);
                    }
                } else {
                    if (res.data.limit_reached) {
                        self.showLimitReachedTooltip(res.message);
                    } else {
                        this.showErrorMessage();
                    }
                }
            })
            .catch((err) => {
                this.showErrorMessage();
                console.log(`Watchlist CTA - ${err.message}`);
            })
            .finally(() => (this._actionTrigger = false));
    }

    watchlistAddXhrCall() {
        const xhrUrl = `${CBS.Registry.region.prefix}/user-profile/xhr/watchlist/${this.contentType}/${this.contentId}/add/`;

        return this._http.doGet(xhrUrl);
    }

    removeFromWatchlist() {
        const self = this;
        let cta = this._cta;

        self.watchlistRemoveXhrCall()
            .then((res) => {
                if (res.success) {
                    cta.classList.remove('hovered');
                    cta.classList.remove('in_watchlist');
                    // tracks removal action
                    if (this._tracking) this._tracking.trackRemoveAction(this.getTrackingParams());
                    // Adds pushstate (history) on watchlist modification
                    if (this.isReferrerHome()) {
                        if (this._isSafari) {
                            window.history.pushState({}, '', document.referrer);
                            return true;
                        }
                        window.history.replaceState({}, document.title, window.location.href);
                    }
                } else {
                    this.showErrorMessage();
                }
            })
            .catch((err) => {
                this.showErrorMessage();
                console.log(`Watchlist CTA - ${err.message}`);
            })
            .finally(() => (this._actionTrigger = false));
    }

    watchlistRemoveXhrCall() {
        const xhrUrl = `${CBS.Registry.region.prefix}/user-profile/xhr/watchlist/${this.contentType}/${this.contentId}/remove/`;
        return this._http.doGet(xhrUrl);
    }

    createTooltipTimeout(tooltip) {
        // If visible already
        if (tooltip.classList.contains('visible')) {
            clearTimeout(this._tooltipTimeout);
        }
        // establish timeout for hiding out the tooltip
        this._tooltipTimeout = setTimeout(
            () => tooltip.classList.remove('visible'),
            this._tooltipTime
        );
    }

    isMobileLandscapeMax() {
        let windowWidth = this._isSafari ? $(window).width() : $(window).outerWidth();
        return windowWidth <= 550;
    }

    isMovie() {
        return this.contentType === CONTENT_MOVIE;
    }

    getTrackingParams() {
        if(this._aaLink !== undefined){
            let aaLinkData = this._aaLink.split('|');

            return {
                pageType: this.isMovie() ? 'video' : 'show',
                showSeriesId: !this.isMovie() ? this.contentId : '',
                showSeriesTitle: !this.isMovie() ? this.watchlistData.content_data.series_title : '',
                showGenre: !this.isMovie() ? this.watchlistData.content_data.genre : '',
                showEpisodeId: !this.isMovie() ? aaLinkData[16] : '',
                showEpisodeTitle: !this.isMovie() ? aaLinkData[5] : '',
                showEpisodeNumber: !this.isMovie() ? aaLinkData[7] : '',
                showSeasonNumber: !this.isMovie() ? aaLinkData[6] : '',
                movieId: this.isMovie() ? this.contentId : '',
                movieTitle: this.isMovie() ? this.watchlistData.content_data.title : '',
                movieGenre: this.isMovie() ? this.watchlistData.content_data.genre : '',
                contentLocked: aaLinkData[23] ? aaLinkData[23] : ''
            };
        }
    }

    initTracking(callback = function () {}) {
        CBS.Promises.Tracking.onLoad(() => {
            this._tracking = new WatchlistTracking(this._cta);
            callback();
        });
    }

    isReferrerHome() {
        const homeUrl = UrlHelper.getHomePageURL().replaceAll('/', '');
        let referrerUrl = document.createElement('a');
        referrerUrl.href = this._referrerUrl;
        referrerUrl = referrerUrl.pathname.replaceAll('/', '');
        return referrerUrl === homeUrl;
    }

    isSafari() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }
}
